// components/Sender/ImportJsonDialog.js
import React, { useRef, useState } from "react";
import { SenderApi } from "./SenderApi";

const KIND_CFG = {
  segments: {
    title: "📥 Импорт сегментов",
    hint: "JSON вида <code>{ segments: [...] }</code> или просто массив сегментов. " +
          "Совместимо с массивом <code>segments</code> из data.json PyQt.",
    payloadKey: "segments",
    api: (arr) => SenderApi.importSegments(arr),
  },
  templates: {
    title: "📥 Импорт шаблонов запусков",
    hint: "JSON вида <code>{ templates: [...] }</code>, <code>{ runs: [...] }</code> " +
          "или просто массив. Совместимо с массивом <code>runs</code> из data.json PyQt.",
    payloadKey: "templates",
    api: (arr) => SenderApi.importTemplates(arr),
  },
};

export function ImportJsonDialog({ kind, onClose, onImported, add }) {
  const cfg = KIND_CFG[kind];
  if (!cfg) throw new Error(`Unknown import kind: ${kind}`);

  const fileRef = useRef(null);
  const [items, setItems] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const handleFile = async (e) => {
    setErr(null); setResult(null); setItems(null);
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      let arr = null;
      if (Array.isArray(data)) arr = data;
      else if (data && typeof data === "object") {
        arr = data[cfg.payloadKey]
           || (kind === "templates" ? data.runs : null)
           || null;
      }
      if (!Array.isArray(arr)) {
        setErr(`В файле нет массива «${cfg.payloadKey}»`);
        return;
      }
      setItems(arr);
    } catch (ex) {
      setErr("Не удалось прочитать JSON: " + ex.message);
    }
  };

  const doImport = async () => {
    if (!items) return;
    setBusy(true); setErr(null);
    try {
      const res = await cfg.api(items);
      const r = res[cfg.payloadKey] || {};
      setResult(r);
      add?.(
        `📥 Импорт ${kind}: +${r.created ?? 0}, ` +
        `обновлено ${r.updated ?? 0}` +
        ((r.errors?.length || 0) > 0 ? `, ошибок ${r.errors.length}` : ""),
        "success"
      );
      onImported?.();
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={card}>
        <div style={headerBar}>
          <div>
            <h3 style={{ margin: 0 }}>{cfg.title}</h3>
            <div style={sub}
                 dangerouslySetInnerHTML={{ __html: cfg.hint }} />
          </div>
          <button className="btn-cancel" onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: 20, overflow: "auto" }}>
          <input ref={fileRef} type="file" accept="application/json"
                 onChange={handleFile} />

          {items && (
            <div style={infoBox}>
              Найдено записей: <b>{items.length}</b>
              <div style={{ marginTop: 6, color: "#6b7280", fontSize: 12 }}>
                Существующие записи с тем же <code>id</code> будут обновлены.
              </div>
            </div>
          )}

          {result && (
            <div style={okBox}>
              ✅ создано: <b>{result.created ?? 0}</b> ·{" "}
              обновлено: <b>{result.updated ?? 0}</b>
              {(result.errors?.length || 0) > 0 && (
                <> · ошибок:{" "}
                  <b style={{ color: "#991b1b" }}>
                    {result.errors.length}
                  </b>
                </>
              )}
            </div>
          )}

          {(err || (result?.errors?.length > 0)) && (
            <div style={errBox}>
              {err || JSON.stringify(result.errors, null, 2)}
            </div>
          )}
        </div>

        <div style={footer}>
          <button className="btn-cancel" onClick={onClose}>Закрыть</button>
          <button className="btn-primary" disabled={!items || busy}
                  onClick={doImport}>
            {busy ? "⏳ Импорт…" : "📥 Импортировать"}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,.6)",
  backdropFilter: "blur(3px)", zIndex: 1200,
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20 };
const card = { background: "#fff", borderRadius: 14, width: "100%",
  maxWidth: 720, maxHeight: "90vh", display: "flex",
  flexDirection: "column", overflow: "hidden" };
const headerBar = { padding: "16px 20px", borderBottom: "1.5px solid #e5e7eb",
  display: "flex", justifyContent: "space-between", alignItems: "center" };
const sub = { fontSize: 12, color: "#6b7280", marginTop: 4 };
const footer = { padding: "12px 20px", borderTop: "1.5px solid #e5e7eb",
  display: "flex", justifyContent: "flex-end", gap: 8 };
const infoBox = { marginTop: 16, padding: 12, borderRadius: 10,
  background: "#f0f9ff", fontSize: 13 };
const okBox = { marginTop: 16, padding: 12, borderRadius: 10,
  background: "#dcfce7", color: "#166534", fontSize: 13, lineHeight: 1.5 };
const errBox = { marginTop: 12, padding: 12, borderRadius: 10,
  background: "#fef2f2", color: "#991b1b", fontSize: 12,
  whiteSpace: "pre-wrap", maxHeight: 200, overflow: "auto" };